/**
 * Quick Actions Widget
 * Provides quick navigation shortcuts for common tasks
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon,
  ClockIcon,
  DocumentTextIcon,
  CogIcon,
  UserPlusIcon,
  ChartBarIcon,
  WrenchScrewdriverIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import { DEFAULT_QUICK_ACTIONS, WIDGET_LAYOUTS } from '../types/widgetTypes';

// Icon mapping for dynamic icon rendering
const ICON_MAP = {
  PlusIcon,
  ClockIcon,
  DocumentTextIcon,
  CogIcon,
  UserPlusIcon,
  ChartBarIcon,
  WrenchScrewdriverIcon,
  BanknotesIcon
};

const QuickActions = ({ 
  actions = DEFAULT_QUICK_ACTIONS, 
  layout = WIDGET_LAYOUTS.RESPONSIVE,
  customTitle = "Quick Actions",
  showTitle = true,
  className = ""
}) => {
  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {showTitle && (
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">{customTitle}</h2>
        </div>
      )}
      <div className="p-6">
        <div className={`grid ${layout} gap-4`}>
          {actions.map((action) => {
            const IconComponent = ICON_MAP[action.icon] || PlusIcon;
            
            return (
              <Link
                key={action.id}
                to={action.href}
                className={`${action.color} text-white rounded-lg p-4 transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5`}
              >
                <div className="flex items-center">
                  <IconComponent className="h-6 w-6 mr-3 flex-shrink-0" />
                  <div className="min-w-0">
                    <h3 className="font-medium text-sm truncate">{action.name}</h3>
                    <p className="text-xs opacity-90 mt-1 leading-tight">
                      {action.description}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Predefined quick action sets
export const ManagementQuickActions = () => (
  <QuickActions
    actions={[
      {
        id: 'view-reservations',
        name: 'View Reservations',
        href: '/reservations',
        icon: 'ClockIcon',
        description: 'Manage all reservations',
        color: 'bg-blue-500 hover:bg-blue-600'
      },
      {
        id: 'manage-customers',
        name: 'Manage Customers',
        href: '/customers',
        icon: 'UserPlusIcon',
        description: 'Customer management',
        color: 'bg-purple-500 hover:bg-purple-600'
      },
      {
        id: 'view-reports',
        name: 'View Reports',
        href: '/reports',
        icon: 'ChartBarIcon',
        description: 'Business analytics',
        color: 'bg-green-500 hover:bg-green-600'
      },
      {
        id: 'system-settings',
        name: 'Settings',
        href: '/settings',
        icon: 'CogIcon',
        description: 'System configuration',
        color: 'bg-gray-500 hover:bg-gray-600'
      }
    ]}
    customTitle="Management Tools"
  />
);

export const OperationalQuickActions = () => (
  <QuickActions
    actions={[
      {
        id: 'new-rental',
        name: 'New Rental',
        href: '/reservations/new',
        icon: 'PlusIcon',
        description: 'Create new reservation',
        color: 'bg-blue-500 hover:bg-blue-600'
      },
      {
        id: 'process-payment',
        name: 'Process Payment',
        href: '/payments',
        icon: 'BanknotesIcon',
        description: 'Handle payments',
        color: 'bg-green-500 hover:bg-green-600'
      },
      {
        id: 'maintenance-check',
        name: 'Maintenance',
        href: '/inventory?filter=maintenance',
        icon: 'WrenchScrewdriverIcon',
        description: 'Equipment maintenance',
        color: 'bg-orange-500 hover:bg-orange-600'
      },
      {
        id: 'daily-report',
        name: 'Daily Report',
        href: '/reports/daily',
        icon: 'DocumentTextIcon',
        description: 'Today\'s summary',
        color: 'bg-purple-500 hover:bg-purple-600'
      }
    ]}
    customTitle="Daily Operations"
  />
);

export default QuickActions;